import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "../../../../../redux/types";
import { trackCartLoad } from "../../../../Analytics/ga/ga";

import { getData } from "../../api/CheckoutView";
import { type AddressData, type UnloadingPointData } from "~/feature/cart/checkout/components/Address";
import { sendData as sendAddressData } from "../../api/CheckoutSelectAddress";
import { CART_PNA_THRESHOLD } from "../../../../../utils/constants";
import { type RootState } from "../../../../../redux/store";
import { orchestrateARefreshOnCheckout } from "./cartRefresh";
import { resolveApiUrl } from "../../../../../utils/url/urlResolver";
import { type CheckoutForm } from "~/feature/cart/checkout/components/CheckoutCart";
import { apiSubmitPlaceOrder, type ErrorCode } from "../../api/placeOrder";
import { selectCartHasPMAProducts } from "../selectors/checkoutPageSelectors";
import { selectCartContainsDigitalServiceTenant } from "../selectors/productEntrySelectors";
import { DeliveryOptionItem } from "~/feature/cart/checkout/components/DeliveryOption";
import { type TransformedCheckoutData } from "../../transformers/CheckoutViewDataTransformer";
import { format } from "~/api/ApiHelper";
import { selectLineItemsByProduct } from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";

export const CHECKOUT_LOAD_DATA = "checkoutPage: load cart data";
export const UPDATE_ADDRESS = "checkoutPage: update selected address";
export const UPDATE_UNLOADING_POINT = "checkoutPage: update selected unloading point";
export const SET_IS_SENDING = "checkoutPage: sending data";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function checkoutPageMounted(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const url = resolveApiUrl("/checkout/multi/summary/viewdata");

    const data = await getData(url);

    dispatch(checkoutLoadDataAction(data));

    const codesToRequest = Object.values(data.productByCode).map((productData) => productData.code);
    if (codesToRequest.length <= CART_PNA_THRESHOLD) {
      dispatch(performCartRefresh());
    }
  };
}

export function checkoutLoadDataAction(data: TransformedCheckoutData) {
  return {
    type: CHECKOUT_LOAD_DATA,
    payload: {
      data,
    },
  } as const;
}

export function trackCheckoutLoadThunk(paymentType: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const { packEntriesById } = state.cart;
    const productByCode = state.products.basic;

    trackCartLoad(Object.values(packEntriesById), productByCode, true, paymentType);
  };
}

function performCartRefresh(): ThunkResult<void> {
  return (dispatch, getState) => {
    const lineItemsByProduct = selectLineItemsByProduct(getState());
    const entries = Object.values(lineItemsByProduct);
    entries.sort((a, b) => a[0].cartEntryNumber - b[0].cartEntryNumber);

    entries.forEach((lineItems) => dispatch(orchestrateARefreshOnCheckout(lineItems, undefined)));
  };
}

export function placeOrderThunk(
  form: CheckoutForm,
  isCustomerInitiatedQuote: boolean,
  unloadingPoint?: UnloadingPointData,
): ThunkResult<Promise<ErrorCode | undefined>> {
  return async (dispatch, getState) => {
    dispatch({
      type: SET_IS_SENDING,
      payload: true,
    });

    const id = (isCustomerInitiatedQuote ? "Q" : "") + format(new Date(), "YYYYDDDDHHmmssSSS");

    const state = getState();
    const { cart, products } = state;
    AnalyticsMQ.instance.push("placeOrder", {
      orderId: id,
      type: isCustomerInitiatedQuote ? "quote" : "purchase",
      paymentType: form.paymentType,
      entries: Object.values(cart.packEntriesById).map((lineItem) => ({
        lineItem,
        product: selectProductDescriptor(state, lineItem),
      })),
    });

    const hasPMA = selectCartHasPMAProducts(state);
    const containsDST = selectCartContainsDigitalServiceTenant(state);
    const freightAccountNumber =
      form?.freightPaymentCode !== "" && form?.freightPaymentAccountNumber !== ""
        ? form?.freightPaymentAccountNumber
        : "";

    const data = {
      poNumber: form.poNumber?.trimLeft(),
      orderInstruction: form.orderComment,
      deliveryMethod: form.deliveryCode,
      isCustomerInitiatedQuote: isCustomerInitiatedQuote,
      email: form.optime?.email ?? "",
      firstName: form.optime?.name ?? "",
      lastName: form.optime?.lastName ?? "",
      plant: form.optime?.plant ?? "",
      timeZone: form.optime?.timeZone ?? "",
      language: form.optime?.language ?? "",
      country: form.optime?.country?.toUpperCase() ?? "",
      hasPMA: hasPMA,
      optime: containsDST,
      unloadingPoint: unloadingPoint?.name ?? "",
      completeDelivery: form.completeDelivery === DeliveryOptionItem.COMPLETE,
      paymentType: form.paymentType,
      creditCardID: form?.creditCardID ?? "",
      freightPaymentCode: form?.freightPaymentCode ?? "",
      freightPaymentAccountNumber: freightAccountNumber,
      orderType: form.orderType ?? "",
      trackUid: id,
    };

    const response = await apiSubmitPlaceOrder(data);

    if (response.success) {
      redirectToUrl(`/checkout/orderConfirmation/${response.orderCode}`);
      return undefined;
    } else {
      dispatch({
        type: SET_IS_SENDING,
        payload: false,
      });
      return response.errorCode;
    }
  };
}

export function updateAddress(newAddress: AddressData, temporary?: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_ADDRESS,
      payload: newAddress,
    });

    dispatch({
      type: SET_IS_SENDING,
      payload: true,
    });

    const finishSending = () =>
      dispatch({
        type: SET_IS_SENDING,
        payload: false,
      });

    sendAddressData(newAddress.id, temporary).then(finishSending).catch(finishSending);
  };
}

export function updateUnloadingPoint(unloadingPoint: UnloadingPointData) {
  return {
    type: UPDATE_UNLOADING_POINT,
    payload: unloadingPoint,
  };
}
